/**
 * A statement, in a drawer, that you read before you run it.
 *
 * Every "New view", "New function", "Add index", "Add trigger", Drop and Edit
 * definition on the Database screen opens this with the statement already
 * written. A form per object would be one DDL generator per object to keep
 * correct — tables and columns, which are always the same few facts, have
 * forms; the rest is SQL, because it is the text that has to end up in a
 * migration anyway.
 *
 * The same drawer is the SQL console, with nothing prefilled: the difference
 * is what it is opened with, not what it does. The console reports and stays
 * open, since it is used more than once in a sitting; anything named closes
 * the moment it succeeds.
 */
import { useEffect, useRef, useState } from 'react';
import { AppDatabaseStatementResult } from '@back4app2/sdk';
import back4app2 from '../../back4app2';
import { DsButton } from '../../components/ds';
import { formatCell, titleFor } from './cells';
import SideDrawer, { DrawerPanel, touchSize } from './SideDrawer';

export interface SqlDialogRequest {
  title: string;
  blurb: string;
  /** What the box opens with. Empty for the console. */
  sql: string;
  /** The button that runs it. "Create" for a New …, "Run" for the console. */
  action: string;
}

const SqlDialog = ({ appId, request, onClose, onDone }: {
  appId: string,
  /** Null when closed — the drawer keeps no state between openings. */
  request: SqlDialogRequest | null,
  onClose: () => void,
  /** A statement succeeded; whatever it changed needs reloading. */
  onDone: () => void,
}) => {
  const [sql, setSql] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [failure, setFailure] = useState<string | undefined>();
  const [result, setResult] = useState<string | undefined>();
  /** What a SELECT came back with, when it came back with anything. */
  const [answer, setAnswer] = useState<AppDatabaseStatementResult | null>(null);
  const boxRef = useRef<HTMLTextAreaElement>(null);
  /** The last request, so the drawer keeps its title while it slides out. */
  const shown = useRef<SqlDialogRequest | null>(request);

  if (request) {
    shown.current = request;
  }

  useEffect(() => {
    if (!request) {
      return;
    }

    setSql(request.sql);
    setFailure(undefined);
    setResult(undefined);
    setAnswer(null);
    // Focused at the end, not selecting the template: the common edit is to
    // change a name inside it, and a selected body is one keystroke from gone.
    window.setTimeout(() => {
      const box = boxRef.current;
      if (box) {
        box.focus();
        box.setSelectionRange(box.value.length, box.value.length);
      }
    }, 280);
  }, [request]);

  const current = shown.current;
  const isDestructive = !!current && /^drop\b/i.test(current.action);
  const size = touchSize();

  const run = () => {
    setIsRunning(true);
    setFailure(undefined);
    setResult(undefined);
    setAnswer(null);

    back4app2
      .runAppDatabaseStatement(appId, sql)
      .then(outcome => {
        if (outcome.errorMessage) {
          // Stays open, with the text intact. A refused statement is a draft,
          // and closing would throw away what needs one edit.
          setFailure(outcome.errorMessage);
          return;
        }

        onDone();

        if (request && request.action === 'Run') {
          setResult(
            [
              outcome.command || 'Done',
              typeof outcome.rowCount === 'number' && outcome.rowCount > 0
                ? `${outcome.rowCount} row${outcome.rowCount === 1 ? '' : 's'}`
                : undefined,
              outcome.isTruncated ? 'first 200 shown' : undefined,
            ].filter(Boolean).join(' · ')
          );
          // A SELECT answers with rows; an UPDATE answers with a number. Only
          // the first has anything to draw.
          setAnswer((outcome.rows || []).length ? outcome : null);
          return;
        }

        onClose();
      })
      .catch((e: Error) => setFailure(e.message))
      .finally(() => setIsRunning(false));
  };

  return (
    <SideDrawer open={!!request} onClose={onClose} width="md:w-[620px]">
      <DrawerPanel
        title={current?.title || ''}
        blurb={current?.blurb}
        onClose={onClose}
        footer={
          <div className="flex items-center justify-end gap-2 flex-wrap ml-auto">
            <DsButton variant="ghost" size={size} onClick={onClose} isDisabled={isRunning}>Cancel</DsButton>
            <DsButton
              variant={isDestructive ? 'danger' : 'primary'}
              size={size}
              onClick={run}
              isDisabled={isRunning || !sql.trim()}
            >
              {isRunning ? 'Running…' : current?.action || 'Run'}
            </DsButton>
          </div>
        }
      >
        <div className="flex flex-col gap-2 h-full min-h-0">
          <label className="flex flex-col gap-2 flex-none">
            <span className="text-ds-135 font-medium text-white">Statement</span>
            <textarea
              ref={boxRef}
              value={sql}
              onChange={event => setSql(event.target.value)}
              spellCheck={false}
              placeholder="SELECT …"
              // It gives the box back when there is an answer to read: the
              // statement has been written by then, and the rows are what the
              // drawer is for.
              className={`b4-focus w-full rounded-control border border-navy-400 bg-navy-950 px-4 py-3 font-courrier text-ds-125 leading-[1.7] text-white placeholder:text-navy-300 focus:border-action outline-none resize-none ${
                answer ? 'h-[140px]' : 'h-[300px]'
              }`}
            />
          </label>

          {failure ? (
            <span className="flex-none font-courrier text-ds-115 leading-[1.5] text-state-err">{failure}</span>
          ) : result && !answer ? (
            // Nothing came back to draw — a CREATE, an UPDATE, a DELETE. It
            // gets a line that reads as an outcome instead of a mono string
            // floating under the box.
            <div className="flex-none flex items-center gap-2.5 px-3 py-2.5 rounded-control border border-state-ok/40 bg-state-ok/10">
              <span className="flex-none w-1.5 h-1.5 rounded-full bg-state-ok" />
              <span className="font-courrier text-ds-115 text-navy-100">{result}</span>
            </div>
          ) : null}

          {answer ? <ResultGrid answer={answer} /> : null}
        </div>
      </DrawerPanel>
    </SideDrawer>
  );
};

/**
 * What the statement answered, as a table.
 *
 * The same shape as the Data grid — mono cells, one line each, the whole value
 * on hover — but without its column types: a SELECT can name an expression,
 * and there is no `dataType` for `count(*)`. So nothing here is right-aligned
 * or parsed; it is what Postgres sent, printed.
 */
const ResultGrid = ({ answer }: { answer: AppDatabaseStatementResult }) => {
  const columns = answer.columns || [];
  const rows = answer.rows || [];

  return (
    <div className="flex-1 min-h-[160px] flex flex-col rounded-control border border-navy-500 bg-navy-900 overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto b4-thin">
      <table className="min-w-full border-collapse">
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={`${column}-${index}`}
                className="sticky top-0 z-[1] bg-navy-700 border-b border-navy-500 border-r border-navy-600 last:border-r-0 px-3 py-2 text-left font-normal whitespace-nowrap font-courrier text-ds-115 text-white"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 ? 'bg-navy-800/50' : ''}>
              {columns.map((column, index) => (
                <td
                  key={`${column}-${index}`}
                  title={titleFor(row[index])}
                  className="px-3 py-[9px] border-b border-r border-navy-700 last:border-r-0 max-w-[280px] truncate whitespace-nowrap font-courrier text-ds-115 text-navy-100"
                >
                  {row[index] === null || row[index] === undefined
                    ? <span className="text-navy-400">null</span>
                    : formatCell(row[index])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* The count belongs to the result, not to the space above it: the same
          strip the Data tab puts under its grid, saying the same thing. */}
      <div className="flex-none flex items-center justify-between gap-3 px-3 py-2 border-t border-navy-600">
        <span className="font-courrier text-ds-115 text-navy-300">
          {answer.command ? `${answer.command} · ` : ''}
          {rows.length} row{rows.length === 1 ? '' : 's'}
        </span>
        {answer.isTruncated ? (
          <span className="font-courrier text-ds-115 text-navy-400">first 200 shown</span>
        ) : null}
      </div>
    </div>
  );
};

export default SqlDialog;
